import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  InputAdornment
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import axios from 'axios'
import Locale from 'date-fns/locale/en-GB'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { office_location } from '../../../shared/enums/office_location'

const defaultFormValues = {
  position: '',
  requested: 1,
  language: '',
  project: '',
  department: '',
  location: '',
  request_type: '',
  due_date: null,
  creationComment: ''
}

const New_Staffing_Request_Modal = ({ getStaffingRequests, closeModal, languages, roles, projects, userLevel }) => {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({ defaultValues: defaultFormValues, mode: 'onTouched' })
  const [departments, setDepartments] = useState([])
  const [managerDepartments, setManagerDepartments] = useState([])
  const [managerProjects, setManagerProjects] = useState([])
  const isAdmin = userLevel === 1
  const isManager = userLevel === 2
  const canSelectProject = isAdmin || isManager

  const getProjectId = (project) => (typeof project === 'object' ? project?._id : project)

  const getManagerProjectsFromDepartments = (departmentList) => {
    const projectsById = new Map()

    departmentList.forEach((department) => {
      const projectId = getProjectId(department.project)

      if (!projectId || projectsById.has(projectId)) return

      projectsById.set(projectId, {
        _id: projectId,
        name: typeof department.project === 'object' ? department.project.name : ''
      })
    })

    return Array.from(projectsById.values())
  }

  const onSubmit = async (data) => {
    const payload = {
      ...data,
      ...(!data.project ? { project: undefined } : {})
    }

    axios
      .post('/staffingRequest', payload)
      .then((res) => {
        if (res.data.success) {
          getStaffingRequests()
          toast.success('Kërkesa u krijua me sukses!')
          closeModal()
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Ndodhi një gabim!')
        console.log('error', err)
      })
  }

  const getAllDepartments = async (projectId = '') => {
    const res = await axios.get('departments', {
      params: {
        fromStaffingRequestModal: true,
        ...(projectId ? { projectId } : {})
      }
    })
    if (res.data.success) {
      setDepartments(res.data.data)
    } else {
      closeModal()
      toast.error('Ndodhi një gabim tek gjuhët!')
    }
  }

  const getManagerDepartments = async () => {
    const res = await axios.get('departments', {
      params: {
        fromStaffingRequestModal: true
      }
    })

    if (res.data.success) {
      const departmentList = res.data.data || []

      setManagerDepartments(departmentList)
      setManagerProjects(getManagerProjectsFromDepartments(departmentList))
    } else {
      closeModal()
      toast.error('Ndodhi një gabim tek departamentet!')
    }
  }

  const handleProjectChange = async (projectId, onChange) => {
    onChange(projectId)
    setValue('department', '')

    if (!projectId) {
      setDepartments([])
      return
    }

    if (isManager) {
      setDepartments(managerDepartments.filter((department) => getProjectId(department.project) === projectId))
      return
    }

    await getAllDepartments(projectId)
  }

  useEffect(() => {
    if (isManager) {
      getManagerDepartments()
      return
    }

    if (!isAdmin) getAllDepartments()
  }, [])

  const renderProjectField = () => (
    <Controller
      name='project'
      control={control}
      defaultValue={defaultFormValues.project}
      render={({ field }) => (
        <FormControl margin='dense' sx={{ width: '45%' }} error={Boolean(errors?.project)}>
          <InputLabel size='small' id='project'>
            Projekti
          </InputLabel>
          <Select
            {...field}
            size='small'
            labelId='project'
            input={<OutlinedInput label='Project' size='small' />}
            onChange={(event) => handleProjectChange(event.target.value, field.onChange)}
          >
            {(isManager ? managerProjects : projects)?.map((project) => (
              <MenuItem key={project._id} value={project._id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error={Boolean(errors?.project)}>{errors?.project?.message || ' '}</FormHelperText>
        </FormControl>
      )}
      rules={canSelectProject ? { required: 'Projekti është i domosdoshëm' } : {}}
    />
  )

  const renderLocationField = () => (
    <Controller
      name='location'
      control={control}
      defaultValue={defaultFormValues.location}
      render={({ field }) => (
        <FormControl margin='dense' sx={{ width: '45%' }} error={Boolean(errors?.location)}>
          <InputLabel size='small' id='location'>
            Lokacioni
          </InputLabel>
          <Select {...field} size='small' labelId='location' input={<OutlinedInput label='Location' size='small' />}>
            {office_location?.map((location) => (
              <MenuItem key={location.value} value={location.value}>
                {location.value}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error={Boolean(errors?.location)}>{errors?.location?.message || ' '}</FormHelperText>
        </FormControl>
      )}
      rules={{ required: 'Lokacioni është i domosdoshëm' }}
    />
  )

  const renderDueDateField = () => (
    <Controller
      name='due_date'
      control={control}
      defaultValue={defaultFormValues.due_date}
      render={({ field }) => (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={Locale}>
          <DatePicker
            {...field}
            label='Data fundit'
            minDate={new Date()}
            inputFormat='dd/MM/yyyy'
            renderInput={(params) => (
              <TextField
                sx={{ width: '45%' }}
                {...params}
                size='small'
                {...params}
                inputProps={{ ...params.inputProps, readOnly: true }}
                helperText={errors?.due_date?.message || ' '}
                error={Boolean(errors?.due_date)}
              />
            )}
          />
        </LocalizationProvider>
      )}
      rules={{ required: 'Data e fundit është e domosdoshme' }}
    />
  )

  const onNumberInputWheel = (e) => {
    try {
      if (e && e.target && e.target.blur) e.target.blur()
    } catch (err) {}
  }

  const onNumberInputFocus = (e) => {
    try {
      const el = e && e.target
      if (el && el.addEventListener) el.addEventListener('wheel', onNumberInputWheel, { passive: true })
    } catch (err) {}
  }

  const onNumberInputBlur = (e) => {
    try {
      const el = e && e.target
      if (el && el.removeEventListener) el.removeEventListener('wheel', onNumberInputWheel)
    } catch (err) {}
  }

  const renderDepartmentField = () => (
    <Controller
      name='department'
      control={control}
      defaultValue={defaultFormValues.department}
      render={({ field }) => (
        <FormControl margin='dense' sx={{ width: '45%' }} error={Boolean(errors?.department)}>
          <InputLabel size='small' id='department'>
            Departamenti
          </InputLabel>
          <Select
            {...field}
            size='small'
            labelId='department'
            input={<OutlinedInput label='Department' size='small' />}
            disabled={canSelectProject && !departments.length}
          >
            {departments?.map((lang) => (
              <MenuItem key={lang._id} value={lang._id}>
                {lang.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error={Boolean(errors?.department)}>{errors?.department?.message || ' '}</FormHelperText>
        </FormControl>
      )}
      rules={{ required: 'Departamenti është i domosdoshëm' }}
    />
  )

  return (
    <Dialog open={true} fullWidth sx={{ fieldset: { border: '1px solid' } }}>
      <DialogTitle>Shto një kërkesë të re për staf</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Controller
            name='position'
            control={control}
            defaultValue={defaultFormValues.position}
            render={({ field }) => (
              <FormControl margin='dense' sx={{ width: '45%' }} error={Boolean(errors?.position)}>
                <InputLabel size='small' id='position'>
                  Pozicioni
                </InputLabel>
                <Select {...field} size='small' labelId='position' input={<OutlinedInput label='Pozicioni' size='small' />}>
                  {roles?.map((role) => (
                    <MenuItem key={role._id} value={role._id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={Boolean(errors?.position)}>{errors?.position?.message || ' '}</FormHelperText>
              </FormControl>
            )}
            rules={{ required: 'Pozicioni është i domosdoshëm' }}
          />
          <Controller
            name='requested'
            control={control}
            defaultValue={defaultFormValues.requested}
            render={({ field }) => (
              <Box sx={{ width: '45%' }}>
                <TextField
                  {...field}
                  sx={{ width: '100%' }}
                  margin='dense'
                  label='Të kërkuar'
                  size='small'
                  variant='outlined'
                  type='number'
                  error={Boolean(errors?.requested)}
                  helperText={errors?.requested?.message || ' '}
                  onFocus={onNumberInputFocus}
                  onBlur={onNumberInputBlur}
                  InputProps={{
                    inputProps: { min: 0 },
                    endAdornment: (
                      <InputAdornment position='end'>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <Button
                            size='small'
                            onClick={() => field.onChange(Math.max(0, (Number(field.value) || 0) - 1))}
                            sx={{
                              minWidth: 30,
                              height: '32px',
                              padding: 0,
                              borderRadius: 0,
                              boxShadow: 'none',
                              bgcolor: 'transparent',
                              color: '#000',
                              fontSize: '14px',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                            }}
                          >
                            -
                          </Button>
                          <Box
                            sx={{
                              width: '1px',
                              height: '20px',
                              backgroundColor: 'rgba(0,0,0,0.23)'
                            }}
                          />
                          <Button
                            size='small'
                            onClick={() => field.onChange((Number(field.value) || 0) + 1)}
                            sx={{
                              minWidth: 30,
                              height: '32px',
                              padding: 0,
                              borderRadius: 0,
                              boxShadow: 'none',
                              bgcolor: 'transparent',
                              color: '#000',
                              fontSize: '14px',
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                            }}
                          >
                            +
                          </Button>
                        </Box>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            )}
            rules={{ required: 'Të kërkuar është e domosdoshme', min: { value: 0, message: 'Minimum value is 0' } }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Controller
            name='language'
            control={control}
            defaultValue={defaultFormValues.language}
            render={({ field }) => (
              <FormControl margin='dense' sx={{ width: '45%' }} error={Boolean(errors?.language)}>
                <InputLabel size='small' id='language'>
                  Gjuha
                </InputLabel>
                <Select {...field} size='small' labelId='language' input={<OutlinedInput label='Language' size='small' />}>
                  {languages?.map((lang) => (
                    <MenuItem key={lang._id} value={lang._id}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={Boolean(errors?.language)}>{errors?.language?.message || ' '}</FormHelperText>
              </FormControl>
            )}
            rules={{ required: 'Gjuha është e domosdoshme' }}
          />
          {canSelectProject ? renderLocationField() : renderDepartmentField()}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {canSelectProject && (
            <>
              {renderProjectField()}
              {renderDepartmentField()}
            </>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {!canSelectProject && (
          <Controller
            name='location'
            control={control}
            defaultValue={defaultFormValues.location}
            render={({ field }) => (
              <FormControl margin='dense' sx={{ width: '45%' }} error={Boolean(errors?.location)}>
                <InputLabel size='small' id='location'>
                  Lokacioni
                </InputLabel>
                <Select {...field} size='small' labelId='location' input={<OutlinedInput label='Location' size='small' />}>
                  {office_location?.map((location) => (
                    <MenuItem key={location.value} value={location.value}>
                      {location.value}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={Boolean(errors?.location)}>{errors?.location?.message || ' '}</FormHelperText>
              </FormControl>
            )}
            rules={{ required: 'Lokacioni është i domosdoshëm' }}
          />
          )}
          <Controller
            name='request_type'
            control={control}
            defaultValue={defaultFormValues.request_type}
            render={({ field }) => (
              <FormControl margin='dense' sx={{ width: '45%' }} error={Boolean(errors?.request_type)}>
                <InputLabel size='small' id='request_type'>
                  Lloji i kërkesës
                </InputLabel>
                <Select {...field} size='small' labelId='request_type' input={<OutlinedInput label='Request Type' size='small' />}>
                  {['Backfill', 'Ramp-Up']?.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={Boolean(errors?.request_type)}>{errors?.request_type?.message || ' '}</FormHelperText>
              </FormControl>
            )}
            rules={{ required: 'Lloji i kërkesës është i domosdoshëm' }}
          />
          {canSelectProject && renderDueDateField()}
        </Box>
        {!canSelectProject && (
        <Controller
          name='due_date'
          control={control}
          defaultValue={defaultFormValues.due_date}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={Locale}>
              <DatePicker
                {...field}
                label='Data fundit'
                minDate={new Date()}
                inputFormat='dd/MM/yyyy'
                renderInput={(params) => (
                  <TextField
                    sx={{ width: '45%' }}
                    {...params}
                    size='small'
                    {...params}
                    inputProps={{ ...params.inputProps, readOnly: true }}
                    helperText={errors?.due_date?.message || ' '}
                    error={Boolean(errors?.due_date)}
                  />
                )}
              />
            </LocalizationProvider>
          )}
          rules={{ required: 'Data e fundit është e domosdoshme' }}
        />
        )}
        <Controller
          name='creationComment'
          control={control}
          defaultValue={defaultFormValues.creationComment}
          render={({ field }) => (
            <TextField {...field} margin='dense' label='Komenti' size='small' variant='outlined' fullWidth multiline maxRows={4} />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} size='small' variant='contained' color='error'>
          Mbrapa
        </Button>
        <Button onClick={handleSubmit(onSubmit)} size='small' variant='contained'>
          Krijo
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default New_Staffing_Request_Modal
